// analytics.ts — kontrakt analityczny CRM widziany od strony Control Center.
//
// Typy są spisane z realnych odpowiedzi backendu (crm/backend/routes/analytics.js), a nie wymyślone.
// Kwoty przychodzą w GROSZACH — nazwa pola zawsze to mówi. Zamiana na złote następuje wyłącznie
// w warstwie prezentacji; żadne obliczenie nie dzieje się na liczbach zmiennoprzecinkowych złotówek.

import type { ApiClient } from './client';

export interface SalesRangeMeta {
  date_from: string;
  date_to: string;
  city?: string | null;
}

export interface SchoolSalesRow {
  /** BIGINT w PostgreSQL → sterownik oddaje ŁAŃCUCH. Typ mówi prawdę, normalizacja w joinKey(). */
  school_id: number | string;
  school_name: string | null;
  city: string | null;
  period_sales_grosze: number;
  period_orders: number;
  period_days: number;
  period_expected_days: number;
}

/**
 * Punkt serii dziennej. Oś czasu to DZIEŃ DOSTAWY (ADR-003) — backend kubełkuje po
 * `delivery_date`, nie po dacie zakupu. Kwota to wartość wg cen katalogowych (ADR-001).
 */
export interface DailyTrendPoint {
  date: string;
  sales_grosze: number;
  orders: number;
}

/** Pozycja słownika do selecta. Backend zwracał to od początku; klient tego nie deklarował. */
export interface SchoolOption {
  school_id: number;
  school_name: string | null;
  city: string | null;
}

export interface SchoolSalesResponse {
  range: SalesRangeMeta;
  count: number;
  summary: { total_sales_grosze: number; total_orders: number; schools_count: number };
  schools: SchoolSalesRow[];
  /**
   * Lista WSZYSTKICH aktywnych szkół — niezależna od filtra i od tego, czy szkoła sprzedała
   * cokolwiek w oknie. Backend liczył ją od początku, a klient jej nie odbierał.
   */
  school_options?: SchoolOption[];
  /**
   * Wykresy. `daily_trend` był liczony po stronie backendu i WYRZUCANY, bo nie istniał
   * w kontrakcie klienta — pole dopisane w R5-15-006 (bezpieczna wygrana parytetu A).
   */
  charts?: {
    /** Projekcja `slim` z backendu — NIE pełny wiersz listy. Patrz SlimChartRow. */
    top_schools: SlimChartRow[];
    bottom_schools: SlimChartRow[];
    daily_trend: DailyTrendPoint[];
  };
}

/**
 * Wiersz OKROJONY, wysyłany wyłącznie w sekcji `charts`.
 *
 * To NIE jest ten sam kształt co pełny wiersz listy — i to jest pułapka, która przez chwilę
 * siedziała w tym pliku. Kontrakt deklarował tu `SchoolSalesRow[]` / `RestaurantSalesRow[]`,
 * podczas gdy backend (crm/backend/routes/analytics.js, funkcja `slim`) wysyła własną projekcję:
 * inne NAZWY pól (`sales_grosze` zamiast `period_sales_grosze`, `orders` zamiast `period_orders`)
 * i brak reszty. Skutek był podstępny: kod czytający `charts.top_schools[i].period_sales_grosze`
 * dostawał `undefined`, formatter zamieniał to na „—", a TypeScript nie zgłaszał NICZEGO,
 * bo typ obiecywał pole, którego w odpowiedzi nigdy nie było.
 *
 * Zły typ jest gorszy niż brak typu: brak typu zmusza do sprawdzenia, zły typ zwalnia ze
 * sprawdzania i myli się cicho.
 */
export interface SlimChartRow {
  school_id?: number;
  restaurant_id?: number;
  school_name?: string | null;
  restaurant_name?: string | null;
  city: string | null;
  /** Wartość w oknie. Uwaga na nazwę — w pełnym wierszu to `period_sales_grosze`. */
  sales_grosze: number;
  /** Liczba zamówień w oknie. W pełnym wierszu: `period_orders`. */
  orders: number;
}

export interface RestaurantSalesRow {
  restaurant_id: number;
  restaurant_name: string | null;
  city: string | null;
  period_sales_grosze: number;
  period_orders: number;
  period_days: number;
}

export interface RestaurantOption {
  restaurant_id: number;
  restaurant_name: string | null;
  city: string | null;
}

export interface RestaurantSalesResponse {
  range: SalesRangeMeta;
  count: number;
  summary: { total_sales_grosze: number; total_orders: number; restaurants_count: number };
  restaurants: RestaurantSalesRow[];
  /** Komplet aktywnych restauracji — niezależny od filtra i od sprzedaży w oknie. */
  restaurant_options?: RestaurantOption[];
  charts?: {
    /** Projekcja `slim` z backendu — NIE pełny wiersz listy. Patrz SlimChartRow. */
    top_restaurants: SlimChartRow[];
    bottom_restaurants: SlimChartRow[];
    daily_trend: DailyTrendPoint[];
  };
}

/** Jakość danych — ta sama skala co w silniku po stronie backendu. */
export type BackendDataQuality = 'GOOD' | 'PARTIAL' | 'LOW_SAMPLE' | 'MISSING_HISTORY' | 'UNRELIABLE_JOIN' | 'UNKNOWN';

export interface DishAggregate {
  /** Liczba POZYCJI zamówienia. System główny zapisuje jedną sztukę na pozycję. */
  units: number;
  /** SUM(qty) — wyłącznie kontrola spójności z `units`; rozjazd obniża jakość danych. */
  qty_sum: number;
  /**
   * Wartość w cenach KATALOGOWYCH (OrderPosition.Amount), przed zniżką szkolną.
   * To NIE jest kwota pobrana od klienta — nazwa pola celowo o tym mówi.
   */
  revenue_list_grosze: number;
  orders: number;
  buyers: number;
  days: number;
  schools: number;
  restaurants: number;
  unit_price_grosze: number | null;
}

export interface DishSalesRow {
  meal_id: number | null;
  meal_name: string | null;
  current: DishAggregate;
  previous: DishAggregate;
  units_delta_pct: number | null;
  revenue_delta_pct: number | null;
  unit_price_delta_pct: number | null;
  repeat_buyers: number;
  repeat_ratio: number | null;
  revenue_share_pct: number | null;
  data_quality: BackendDataQuality;
  data_quality_reason: string;
}

export type InsightType =
  | 'TOP_DISH'
  | 'FASTEST_GROWING_DISH'
  | 'FASTEST_DECLINING_DISH'
  | 'LOW_PERFORMER'
  | 'PRICE_CHANGE_SIGNAL'
  | 'HIGH_REPEAT_PRODUCT'
  | 'SCHOOL_OUTLIER';

/**
 * Deklaracja rankingu — obowiązkowa dla każdego insightu, który TWIERDZI O SKRAJNOŚCI.
 *
 * Zdanie „największy spadek" jest sprawdzalne dopiero wtedy, gdy wiadomo: największy WEDŁUG
 * CZEGO i W JAKIM ZBIORZE. Bez tego walidator musiałby zgadywać metrykę z prozy — a zgadywanie
 * z prozy jest dokładnie tym, przed czym ten kontrakt chroni.
 *
 * `null` znaczy: ten insight nie twierdzi o skrajności. NIE znaczy „nie wiadomo".
 */
export interface DeklaracjaRankingu {
  /** Metryka, po której backend faktycznie posortował. Nie opis, tylko klucz. */
  metric: string;
  /** MAX — „największy"; MIN — „najmniejszy"/„największy spadek". */
  direction: 'MAX' | 'MIN';
  /** Zbiór, w którym byt jest skrajny. Superlatyw bez zbioru nie znaczy nic. */
  source: string;
}

export interface Insight {
  insight_id: string;
  type: InsightType;
  entity_type: string;
  entity_id: number | null;
  entity_name: string | null;
  title: string;
  fact: string;
  why_it_matters: string;
  recommended_next_step: string;
  evidence: Record<string, number | null>;
  metric_values: Record<string, number | null>;
  time_window: { date_from: string; date_to: string };
  comparison_window: { date_from: string; date_to: string };
  sample_size: number;
  data_quality: BackendDataQuality;
  /** Patrz DeklaracjaRankingu. null = insight nie twierdzi o skrajności. */
  ranking?: DeklaracjaRankingu | null;
  hypothesis: string | null;
  suggested_test: string | null;
  /** Backend deklaruje to w każdym insighcie. Jeśli kiedykolwiek przyjdzie co innego — to jest błąd. */
  causation: 'NOT_ESTABLISHED';
  engine_version: string;
}

/** Szkoła, w której udział dania jest wyraźnie niższy niż mediana pozostałych szkół restauracji. */
export interface SchoolOutlier {
  meal_id: number;
  meal_name: string | null;
  school_id: number;
  school_name: string | null;
  units: number;
  school_total_units: number;
  share: number;
  median_share_other_schools: number;
  share_ratio: number;
  other_schools: number;
}

export interface DishSalesResponse {
  range: { date_from: string; date_to: string; comparison: { date_from: string; date_to: string }; days: number };
  count: number;
  returned: number;
  summary: { total_revenue_list_grosze: number; total_units: number; dishes_count: number };
  dishes: DishSalesRow[];
  insights: Insight[];
  school_outliers: SchoolOutlier[];
  engine_version: string;
  caveats: string[];
}

export interface SalesQuery {
  date_from?: string;
  date_to?: string;
  city?: string;
  restaurant_id?: number;
  school_id?: number;
  top_n?: number;
}

export function createAnalyticsApi(client: ApiClient) {
  return {
    schoolSales: (q: SalesQuery = {}) =>
      client.get<SchoolSalesResponse>('crm', '/api/crm/analytics/school-sales', { ...q }),
    restaurantSales: (q: SalesQuery = {}) =>
      client.get<RestaurantSalesResponse>('crm', '/api/crm/analytics/restaurant-sales', { ...q }),
    dishSales: (q: SalesQuery = {}) =>
      client.get<DishSalesResponse>('crm', '/api/crm/analytics/dish-sales', { ...q }),

    // O1 — rejestr pojedynczej platnosci. Osobna rodzina, bo inne ziarno i inne ryzyko.
    //
    // `/api/crm/transactions` to DEDYKOWANY router `crm/backend/routes/transactions.js`
    // (server.js:102), a nie trasa o tej nazwie w `read.js` — tamta jest ZASŁONIĘTA, bo goły
    // `/api/crm` montuje się dopiero na końcu (server.js:132). Różnica jest istotna: zasłonięta
    // trasa dołącza `crm_customers` po imię i e-mail, dedykowany router świadomie tego nie robi
    // i zwraca `buyer_identifier_safe`. To ten drugi odpowiada i to on jest kontraktem CC.
    transactions: (q: TransactionQuery = {}) =>
      client.get<TransactionListResponse>('crm', '/api/crm/transactions', { ...q }),
    transactionDetail: (id: number) =>
      client.get<TransactionDetail>('crm', `/api/crm/transactions/${id}`),

    // Parytet ze starym panelem — ZIARNO POZYCJI. Trasa WŁASNA Control Center, nie montowany
    // router CRM: granica własności zabrania zmieniać pliki CRM, a tego widoku tam nie ma.
    //
    // Adres podany wprost jako `/api/cc/…`, a nie przez tabelę przekładu w `trasowanie.ts`:
    // ta tabela tłumaczy ścieżki ŹRÓDEŁ na ścieżki Control Center, a ta trasa źródła nie ma.
    // Dopisanie jej tam sugerowałoby, że w CRM istnieje odpowiednik. Nie istnieje.
    transakcjeParytet: (q: ParytetZapytanie = {}) =>
      client.get<ParytetOdpowiedz>('crm', '/api/cc/crm/parytet/transakcje', { ...q }),
    transakcjeParytetSlowniki: () =>
      client.get<ParytetSlowniki>('crm', '/api/cc/crm/parytet/transakcje/slowniki'),

    opinie: (q: OpinieZapytanie = {}) =>
      client.get<OpinieOdpowiedz>('crm', '/api/cc/crm/opinie', { ...q }),

    // ── ADMINISTRACJA (OWNER 2026-10-10) ──────────────────────────────────────────────────────
    // Trzy ekrany na API systemu głównego, przez trasy własne CC pod `/admin/` (tylko admin).
    // Zapisy to działania operacyjne z jawnej listy (`client.ts`) — serwer i tak rozstrzyga.

    // Użytkownicy = stary panel „Konta". Pełna lista zostaje na serwerze CC; tu przychodzi strona.
    // Pierwszy odczyt pełnej listy z systemu głównego bywa długi — stąd dłuższy limit czasu.
    // POST, nie GET: frazy (imię, e-mail, telefon — także uczniów) jadą w treści, nie w adresie, który
    // ląduje w dziennikach nginx i w historii przeglądarki. Serwer niczego tu nie zapisuje.
    uzytkownicy: (q: ZapytanieUzytkownikow = {}) =>
      client.dzialanieOperacyjne<UzytkownicyOdpowiedz>('crm', '/api/cc/admin/uzytkownicy/szukaj', {
        method: 'POST', body: { ...q }, timeoutMs: 120_000,
      }),
    uzytkownicyDoladuj: (cialo: { uzytkownikId: string; kwota: string; operacjaId: string; haslo: string; loginOperatora: string }) =>
      client.dzialanieOperacyjne<DoladowanieWynik>('crm', '/api/cc/admin/uzytkownicy/doladuj', {
        method: 'POST', body: cialo, timeoutMs: 180_000,
      }),
    uzytkownicyUsun: (cialo: {
      uzytkownikId: string; typKonta: 'Klient' | 'Restaurator'; potwierdzenie: string; haslo: string; loginOperatora: string;
    }) =>
      client.dzialanieOperacyjne<UsuniecieKontaWynik>('crm', '/api/cc/admin/uzytkownicy/usun', {
        method: 'POST', body: cialo, timeoutMs: 180_000,
      }),
    uzytkownicyAudyt: () =>
      client.get<AudytAdministracjiOdpowiedz>('crm', '/api/cc/admin/uzytkownicy/audyt'),

    // Szkoły i restauracje = panel restauracji „Szkoły" (dodanie) + „Usuń" ze starego Grafiku admina.
    // Relacja czytana tym samym wywołaniem co Grafik — po zmianie oba ekrany widzą to samo.
    adminSzkolyRestauracji: () =>
      client.get<SzkolyIRestauracjeOdpowiedz>('crm', '/api/cc/admin/szkoly-restauracji', undefined, { timeoutMs: 60_000 }),
    adminPrzypiszSzkole: (cialo: { restauracjaId: number; szkolaId: number; godzinaZamkniecia?: string }) =>
      client.dzialanieOperacyjne<PrzypisanieSzkolyWynik>('crm', '/api/cc/admin/szkoly-restauracji/przypisz', {
        method: 'POST', body: cialo, timeoutMs: 60_000,
      }),
    adminOdepnijSzkole: (cialo: { restauracjaId: number; szkolaId: number }) =>
      client.dzialanieOperacyjne<OdpiecieSzkolyWynik>('crm', '/api/cc/admin/szkoly-restauracji/odepnij', {
        method: 'POST', body: cialo, timeoutMs: 60_000,
      }),
    adminSzkolyRestauracjiAudyt: () =>
      client.get<AudytAdministracjiOdpowiedz>('crm', '/api/cc/admin/szkoly-restauracji/audyt'),

    // Moderacja menu = stary panel admina „Menu": cztery zakładki, te same wywołania i statusy.
    moderacjaLiczniki: () =>
      client.get<LicznikiModeracji>('crm', '/api/cc/admin/moderacja-menu/liczniki'),
    moderacjaKolejka: (q: { strona?: number; rozmiar?: number } = {}) =>
      client.get<StronaPropozycji>('crm', '/api/cc/admin/moderacja-menu/kolejka', { ...q }),
    moderacjaZatwierdzonePrzezAi: (q: { strona?: number; rozmiar?: number } = {}) =>
      client.get<StronaPropozycji>('crm', '/api/cc/admin/moderacja-menu/zatwierdzone-przez-ai', { ...q }),
    moderacjaHistoria: (q: { strona?: number; rozmiar?: number; filtr?: FiltrHistoriiModeracji } = {}) =>
      client.get<StronaPropozycji>('crm', '/api/cc/admin/moderacja-menu/historia', { ...q }),
    moderacjaDoPotwierdzenia: () =>
      client.get<{ dania: DanieDoPotwierdzenia[] }>('crm', '/api/cc/admin/moderacja-menu/do-potwierdzenia'),
    moderacjaZatwierdz: (cialo: { rodzaj: RodzajDecyzjiModeracji; id: number }) =>
      client.dzialanieOperacyjne<DecyzjaModeracjiWynik>('crm', '/api/cc/admin/moderacja-menu/zatwierdz', {
        method: 'POST', body: cialo,
      }),
    moderacjaOdrzuc: (cialo: { rodzaj: RodzajDecyzjiModeracji; id: number }) =>
      client.dzialanieOperacyjne<DecyzjaModeracjiWynik>('crm', '/api/cc/admin/moderacja-menu/odrzuc', {
        method: 'POST', body: cialo,
      }),
    moderacjaAudyt: () =>
      client.get<AudytAdministracjiOdpowiedz>('crm', '/api/cc/admin/moderacja-menu/audyt'),

    // Restauracje → Rozliczenia tygodniowe. Trasa własna CC pod `/admin/` (tylko admin), tylko
    // odczyt. Bez tygodnia serwer wybiera domyślny (ostatni zakończony, wg daty w Warszawie).
    rozliczenia: (t?: { rokIso: number; tydzien: number }) =>
      client.get<RozliczeniaOdpowiedz>('crm', '/api/cc/admin/rozliczenia',
        t ? { year: t.rokIso, week: t.tydzien } : {}),

    // Restauracje → Rozliczenia MIESIĘCZNE (faktury TuSzamy): suma rozliczeń tygodniowych z tygodni
    // przypisanych do miesiąca (tydzień należy do miesiąca z większością dni pon–pt). Bez miesiąca
    // serwer wybiera ostatni zamknięty.
    rozliczeniaMiesiac: (m?: { rok: number; miesiac: number }) =>
      client.get<RozliczeniaMiesiacOdpowiedz>('crm', '/api/cc/admin/rozliczenia/miesiac',
        m ? { year: m.rok, month: m.miesiac } : {}),

    // Restauracje → Rozliczenia ROCZNE (arkusz OWNERA „Wykresy Obrotów i Zysków”): rok rozliczeniowy
    // IX..VIII z miesięcy ekranu miesięcznego. `start` = rok początku (2026 = 2026/2027); bez niego bieżący.
    rozliczeniaRok: (start?: number) =>
      client.get<RozliczeniaRokOdpowiedz>('crm', '/api/cc/admin/rozliczenia/rok',
        start !== undefined ? { start } : {}),

    // Restauracje → Grafik. Kanoniczny grafik z systemu głównego: odczyt i zapis przez to samo API,
    // którego używa stary panel. Zapis to działanie operacyjne z jawnej listy (`client.ts`).
    grafikKanoniczny: () =>
      client.get<GrafikKanonicznyOdpowiedz>('crm', '/api/cc/admin/grafik'),
    grafikZmiany: (zmiany: GrafikZmiana[]) =>
      client.dzialanieOperacyjne<GrafikZmianyOdpowiedz>('crm', '/api/cc/admin/grafik/zmiany', {
        method: 'POST',
        body: { zmiany },
      }),
    grafikAudyt: () =>
      client.get<GrafikAudytOdpowiedz>('crm', '/api/cc/admin/grafik/audyt'),

    // Panel Restauracji. `restauracja` jest tu WSKAZANIEM, nie uprawnieniem — serwer i tak
    // sprawdza je względem tożsamości i odmawia, gdy wskazuje cudzą restaurację.
    dzisiejszeZamowienia: (restauracja?: number, dzien?: string) =>
      client.get<DzisiejszeZamowienia>('crm', '/api/cc/restauracja/dzisiaj',
        { ...(restauracja ? { restauracja } : {}), ...(dzien ? { dzien } : {}) }),
    szkolyRestauracji: (restauracja?: number) =>
      client.get<SzkolyRestauracji>('crm', '/api/cc/restauracja/szkoly',
        restauracja ? { restauracja } : {}),
    menuDnia: (data: string, restauracja?: number) =>
      client.get<MenuDnia>('crm', '/api/cc/restauracja/menu-dnia',
        { data, ...(restauracja ? { restauracja } : {}) }),
    daniaRestauracji: (restauracja?: number) =>
      client.get<DaniaRestauracji>('crm', '/api/cc/restauracja/dania',
        restauracja ? { restauracja } : {}),
    // Raporty dostaw — dane systemu kierowców, zawężone po stronie serwera do jednej
    // restauracji. `restauracja` jest tu WSKAZANIEM dla konta właściciela, nie uprawnieniem.
    raportDostawPodsumowanie: (z: ZapytanieRaportuDostaw = {}) =>
      client.get<PodsumowanieDostaw>('drive', '/api/cc/restauracja/dostawy/podsumowanie', paramsRaportu(z)),
    raportDostawKierowcy: (z: ZapytanieRaportuDostaw = {}) =>
      client.get<KierowcyDostaw>('drive', '/api/cc/restauracja/dostawy/kierowcy', paramsRaportu(z)),
    raportDostawSzkoly: (z: ZapytanieRaportuDostaw = {}) =>
      client.get<SzkolyDostaw>('drive', '/api/cc/restauracja/dostawy/szkoly', paramsRaportu(z)),
    raportDostawDni: (z: ZapytanieRaportuDostaw = {}) =>
      client.get<DniDostaw>('drive', '/api/cc/restauracja/dostawy/dni', paramsRaportu(z)),
    raportDostawLista: (z: ZapytanieRaportuDostaw = {}) =>
      client.get<ListaDostaw>('drive', '/api/cc/restauracja/dostawy/lista', paramsRaportu(z)),
    raportDostawFiltry: (z: ZapytanieRaportuDostaw = {}) =>
      client.get<FiltryDostaw>('drive', '/api/cc/restauracja/dostawy/filtry', paramsRaportu(z)),

    transakcjeRestauracji: (zapytanie: ZapytanieTransakcjiRestauracji = {}) =>
      client.get<TransakcjeRestauracji>('crm', '/api/cc/restauracja/transakcje', {
        ...(zapytanie.restauracja ? { restauracja: zapytanie.restauracja } : {}),
        ...(zapytanie.od ? { od: zapytanie.od } : {}),
        ...(zapytanie.do ? { do: zapytanie.do } : {}),
        ...(zapytanie.status ? { status: zapytanie.status } : {}),
        ...(zapytanie.skasowane ? { skasowane: zapytanie.skasowane } : {}),
        ...(zapytanie.strona && zapytanie.strona > 1 ? { strona: zapytanie.strona } : {}),
      }),

    // Zdolności środowiska — jedno zapytanie, z którego korzystają wszystkie ekrany.
    zdolnosci: () => client.get<Zdolnosci>('crm', '/api/cc/zdolnosci'),

    // Podglad restauracji. Haslo idzie WYLACZNIE w ciele POST-a — nigdy w adresie.
    szukajRestauracji: (login: string) =>
      client.get<WynikSzukania>('crm', '/api/cc/admin/podglad/szukaj', { login }),
    stanPodgladu: () => client.get<StanPodgladu>('crm', '/api/cc/admin/podglad/stan'),

    /**
     * Wejście właściciela w panel restauracji. Login restauracji WSKAZUJE cel, hasło
     * właściciela DOWODZI uprawnienia. Hasła restauracji nie znamy i nie jest potrzebne.
     *
     * Idzie `sessionRequest`, bo to jest logowanie — a nie działanie na danych.
     */
    logowanieMistrza: (login: string, haslo: string) =>
      client.sessionRequest<LogowanieMistrza>('crm', '/api/cc/restauracja/logowanie-mistrza', {
        method: 'POST',
        // Hasło jedzie w ciele i tylko tam. Adres zostaje w historii przeglądarki,
        // w nagłówku `Referer` i w dziennikach serwerów pośredniczących.
        body: { login, haslo },
      }),
    wejdzWPodglad: (restauracjaId: number, haslo: string) =>
      client.dzialanieOperacyjne<WejscieWPodglad>('crm', '/api/cc/admin/podglad/wejdz', {
        method: 'POST',
        // Hasło jedzie w ciele i tylko tam. Nie zapisujemy go, nie logujemy, nie trzymamy w stanie.
        body: { restauracja_id: restauracjaId, haslo },
      }),
    zakonczPodglad: () =>
      client.dzialanieOperacyjne<{ aktywny: boolean }>('crm', '/api/cc/admin/podglad/zakoncz', {
        method: 'POST', body: {},
      }),

    // Katalog dan — REJESTR. Danie bez sprzedazy tu JEST.
    dishCatalog: (q: { restaurant_id?: number; search?: string } = {}) =>
      client.get<DishCatalogResponse>('crm', '/api/crm/dictionary/dishes', { ...q }),

    // Słowniki REFERENCYJNE. Osobne od wymiarów sprzedaży o tych samych nazwach: tamte mówią,
    // w jakich szkołach coś KUPIONO, te — jakie szkoły w ogóle istnieją.
    schoolsDictionary: () =>
      client.get<SchoolsDictionaryResponse>('crm', '/api/crm/dictionary/schools'),
    restaurantsDictionary: () =>
      client.get<RestaurantsDictionaryResponse>('crm', '/api/crm/dictionary/restaurants'),
  };
}

export type AnalyticsApi = ReturnType<typeof createAnalyticsApi>;

// ── RESTAURACJE → ROZLICZENIA TYGODNIOWE ─────────────────────────────────────
//
// Kwoty w GROSZACH (liczby całkowite). Obrót dokładny co do grosza; prowizja, VAT i wypłata
// zaokrąglone RAZ do pełnych złotych na poziomie rozliczenia (OWNER 2026-10-02). Dokładne
// wartości przed zaokrągleniem są w `exact` jako tekst dziesiętny — nigdy jako float.

/** Dlaczego rozliczenie nie ma kwot: brak dowodu kwoty pobranej albo brak stawki restauracji. */
export type PowodBrakuKwot = 'AMOUNT_NOT_PROVABLE' | 'MARGIN_UNKNOWN';

/** Powód, dla którego stan repliki nie jest świeży. */
export type PowodNieswiezosci = 'FRESHNESS_UNKNOWN' | 'FULL_SYNC_STALE' | 'INCREMENTAL_SYNC_STALE' | 'SYNC_FAILED';

export interface SwiezoscRozliczen {
  state: 'FRESH' | 'STALE';
  warnings: PowodNieswiezosci[];
  lastFullSync: string | null;
  lastIncrementalSync: string | null;
  incrementalWindowFrom: string | null;
  lastSyncError: string | null;
  incrementalScheduleActiveNow: boolean;
  checkedAt: string;
  rules: {
    fullSyncMaxAgeHours: number;
    incrementalMaxAgeMinutes: number;
    incrementalSchedule: string;
    fullSchedule: string;
  };
}

export interface KwotyDokladne {
  prowizjaNetto: string;
  vat: string;
  prowizjaBrutto: string;
  doWyplaty: string;
}

/** Pięć liczb: PON, WT, ŚR, CZW, PT. */
export type PiecDni = [number, number, number, number, number];

export interface CzlonekRozliczenia {
  restaurantId: number;
  name: string;
  city: string | null;
  blocked: boolean;
  marginNet: number | null;
  turnoverByDayGrosze: PiecDni;
  turnoverGrosze: number;
  positionsByDay: PiecDni;
  positions: number;
  positionsWithoutAmount: number;
  exact: KwotyDokladne | null;
}

export interface Rozliczenie {
  /** `grupa:<id>` albo `restauracja:<id>` — stabilny klucz wiersza. */
  settlementGroupId: string;
  settlementGroupName: string;
  isGroup: boolean;
  cities: string[];
  turnoverByDayGrosze: PiecDni;
  turnoverGrosze: number;
  turnoverComplete: boolean;
  positionsByDay: PiecDni;
  positions: number;
  /** Stawka marży netto, gdy jedna dla całego rozliczenia; `null` = różne albo nieznana. */
  commissionNetRate: number | null;
  marginsNet: number[];
  commissionVatRate: number;
  commissionNetGrosze: number | null;
  commissionVatGrosze: number | null;
  commissionGrossGrosze: number | null;
  payoutGrosze: number | null;
  exact: KwotyDokladne | null;
  amountsUnavailableReason: PowodBrakuKwot | null;
  members: CzlonekRozliczenia[];
  /** Kwalifikacja do listy (filtr aktywnych restauracji): obrót kanoniczny od 2026-09-01 do dziś (Warszawa). */
  qualification?: { qualified: boolean; turnoverSinceGrosze: number; positionsWithoutAmountSince: number };
}

/** Filtr aktywnych restauracji (OWNER 2026-10-03): widoczne WYŁĄCZNIE przy obrocie od 1.09.2026 do dziś > 0. */
export interface WidocznoscRozliczen {
  rule: string;
  /** 'VISIBLE_SETTLEMENTS' — „Razem” sumuje wyłącznie widoczne rozliczenia. */
  totalsScope?: string;
  qualificationFrom: string;
  qualificationTo: string;
  visibleSettlements: number;
  hiddenSettlements: number;
  hidden: { settlementGroupId: string; settlementGroupName: string; positionsWithoutAmountSince?: number }[];
}

export interface RozliczeniaOdpowiedz {
  period: {
    isoYear: number;
    isoWeek: number;
    weeksInYear: number;
    monday: string;
    friday: string;
    days: { key: 'pon' | 'wt' | 'sr' | 'czw' | 'pt'; date: string }[];
    isDefault: boolean;
    dateRule: 'DELIVERY_DATE_MON_FRI';
  };
  rules: {
    commissionVatRate: number;
    warsaw: { cityId: number; city: string; marginNet: number; payoutMultiplier: number };
    otherCities: { marginNet: number; payoutMultiplier: number };
    rounding: 'FINAL_SETTLEMENT_TO_WHOLE_PLN';
    turnoverPrecision: 'GROSZ';
    turnoverRule: string;
    marginEditable: false;
  };
  source: {
    amountState: 'COMPLETE' | 'INCOMPLETE';
    /** Która reguła liczy kwotę pozycji. Kolumna #791 tylko po jawnym otwarciu bramki aktywacji. */
    amountRule: 'LIST_PRICE_ONLY_WHEN_TRANSACTION_PAID_EQUALS_AMOUNT' | 'PAID_AMOUNT_COLUMN';
    hasPaidAmountColumn: boolean;
    paidAmountColumnActive: boolean;
    positions: number;
    positionsWithPaidAmount: number;
    positionsWithListPriceNoDiscount: number;
    positionsWithoutAmount: number;
    positionsWithoutRestaurant: number;
    sync: {
      readable: boolean;
      lastFullSync: string | null;
      lastIncrementalSync: string | null;
      incrementalWindowFrom: string | null;
      lastBatch: string | null;
      lastError: string | null;
    };
  };
  /** Czy stanowi repliki wolno ufać (serwer liczy to z przebiegów synchronizacji). */
  freshness: SwiezoscRozliczen;
  settlements: Rozliczenie[];
  /** Brak pola = serwer sprzed filtra (wszystkie rozliczenia widoczne). */
  visibility?: WidocznoscRozliczen;
  totals: {
    turnoverByDayGrosze: PiecDni;
    turnoverGrosze: number;
    turnoverComplete: boolean;
    positions: number;
    commissionNetGrosze: number | null;
    commissionVatGrosze: number | null;
    commissionGrossGrosze: number | null;
    payoutGrosze: number | null;
    settlementsWithoutAmounts: number;
  };
  correlation_id?: string;
}

// ── O1: rejestr pojedynczej płatności ────────────────────────────────────────
// Kontrakt świadomie NIE zawiera imienia, nazwiska, e-maila ani telefonu kupującego.
// Do odnalezienia płatności przy reklamacji wystarcza identyfikator (minimum konieczne).

export interface TransactionListRow {
  transaction_id: number;
  order_id: number | null;
  created_at: string | null;
  order_date: string | null;
  fulfillment_date: string | null;
  historical_school_id: number | null;
  historical_school_name: string | null;
  restaurant_id: number | null;
  restaurant_name: string | null;
  buyer_identifier_safe: string | null;
  status: string | null;
  transaction_type: number | null;
  /** Suma cen KATALOGOWYCH (przed zniżką szkolną). NIE jest to kwota, która wpłynęła. */
  list_value_total_grosze: number | null;
  /** Kwota FAKTYCZNIE POBRANA. `null` = nie wiemy, NIGDY nie znaczy zero. */
  paid_amount_grosze: number | null;
  /** Różnica — wyłącznie gdy obie kwoty są znane. Inaczej `null`. */
  discount_difference_grosze: number | null;
  missing_fields: string[];
}

export interface TransactionListResponse {
  count: number;
  total: number;
  limit: number;
  truncated: boolean;
  transactions: TransactionListRow[];
}

export interface TransactionItem {
  order_item_id: number;
  dish_id: number | null;
  /** Nazwa z katalogu, jeśli danie w nim jest; inaczej snapshot z zamówienia. */
  dish_name: string | null;
  /** Nazwa zapisana w chwili zakupu — katalog mógł ją później zmienić. */
  dish_name_snapshot: string | null;
  quantity: number | null;
  historical_list_price_grosze: number | null;
  line_list_value_grosze: number | null;
  restaurant_id: number | null;
  is_deleted: boolean;
  /** ZAWSZE null — źródło nie zna kwoty pobranej na ziarnie pozycji. */
  paid_amount_grosze: null;
  paid_amount_note: string;
}

export interface TransactionDetail extends TransactionListRow {
  order_items: TransactionItem[];
  order_items_count: number;
  caveats: string[];
}

export interface TransactionQuery {
  date_from?: string;
  date_to?: string;
  school_id?: number;
  restaurant_id?: number;
  status?: string;
  transaction_id?: number;
  limit?: number;
}

// ── PARYTET ZE STARYM PANELEM: ZIARNO POZYCJI ZAMÓWIENIA ─────────────────────
//
// Osobna rodzina od `TransactionListRow`, bo to INNE ZIARNO, a nie inny widok tego samego.
// Tam jeden wiersz = jedna transakcja; tutaj jeden wiersz = jedna pozycja zamówienia —
// dokładnie tak, jak liczy stary panel (`orderPositionId` jest tam kluczem wiersza).

/** Skąd wzięła się kwota w wierszu — i czy w ogóle jest. */
export type StanKwoty =
  /** Wartość zreplikowana ze źródła. To jest legacy „Kwota". */
  | 'ZNANA'
  /** Kolumna istnieje, ale ten wiersz nie ma jeszcze wartości (backfill w toku). */
  | 'NIEZSYNCHRONIZOWANA'
  /** Kolumny jeszcze nie ma — zależność od strumienia R5-04. */
  | 'KOLUMNA_NIE_ISTNIEJE';

export interface ParytetWiersz {
  /** Techniczne — do zaznaczania i akcji. NIE jest kolumną widoczną. */
  pozycja_id: number;
  zamowienie_id: number | null;
  transakcja_id: number | null;

  data: string | null;
  data_dostawy: string | null;
  klient: string | null;
  szkola: string | null;
  szczegoly: string | null;
  ilosc: number | null;
  dostawca: string | null;

  /** Kwota FAKTYCZNIE POBRANA. `null` = nie wiemy. NIGDY nie znaczy zero. */
  kwota_grosze: number | null;
  kwota_stan: StanKwoty;

  status: string | null;
  typ: string | null;
  pozycja_skasowana: boolean;
}

export interface ParytetSuma {
  grosze: number | null;
  /** `false` = część wierszy nie ma jeszcze kwoty. Suma jest wtedy NIEPEŁNA. */
  kompletna: boolean;
  wierszy_liczonych: number;
  wierszy_bez_kwoty: number;
  podstawa: string;
}

export interface ParytetOdpowiedz {
  ziarno: 'POZYCJA_ZAMOWIENIA';
  wiersze: ParytetWiersz[];
  strona: number;
  rozmiar_strony: number;
  wierszy_ogolem: number;
  suma: ParytetSuma;
  zrodlo_kwoty: string | null;
  zaleznosc: { strumien: string; czego_brak: string; skutek: string } | null;
  sort: string;
  kierunek: 'ASC' | 'DESC';
}

export interface ParytetZapytanie {
  date_from?: string;
  date_to?: string;
  klient?: string;
  szkola?: string;
  dostawca?: string;
  kwota?: string;
  status?: string;
  typ?: string;
  sort?: string;
  kierunek?: 'ASC' | 'DESC';
  strona?: number;
}

// ── ADMINISTRACJA → UŻYTKOWNICY (stary panel „Konta") ──────────────────────

/** Role kont z systemu głównego (`GetUserType()`) — bez nowych. */
export type RolaKonta = 'Administrator' | 'Klient' | 'Restaurator';

export interface KontoUzytkownika {
  /** GUID konta w systemie głównym. */
  id: string;
  rola: RolaKonta | null;
  imie: string | null;
  email: string | null;
  telefon: string | null;
  miasto: string | null;
  miastoId: number | null;
  szkola: string | null;
  utworzono: string | null;
  transakcjeGrosze: number;
  transakcjeLiczba: number;
  saldoGrosze: number;
  /** `null` = brak daty (stary panel pokazywał wtedy „1.01.1970"). */
  waznoscSrodkow: string | null;
  potwierdzony: boolean;
  zablokowany: boolean;
}

export type SortowanieKont =
  | 'utworzono' | 'imie' | 'rola' | 'email' | 'miasto' | 'szkola' | 'saldo' | 'transakcje' | 'potwierdzony' | 'zablokowany';

export interface ZapytanieUzytkownikow {
  /** Szybkie wyszukiwanie: imię, e-mail, telefon, szkoła, miasto, ID — bez wielkości liter i ogonków. */
  q?: string;
  imie?: string;
  email?: string;
  telefon?: string;
  rola?: RolaKonta | '';
  miasto?: string;
  szkola?: string;
  potwierdzony?: 'tak' | 'nie' | '';
  zablokowany?: 'tak' | 'nie' | '';
  sort?: SortowanieKont;
  kierunek?: 'rosnaco' | 'malejaco';
  strona?: number;
  rozmiar?: number;
  odswiez?: 'tak';
}

export interface UzytkownicyOdpowiedz {
  konta: KontoUzytkownika[];
  strona: number;
  rozmiar: number;
  stron: number;
  /** Wynik filtrów na PEŁNYM zbiorze (nie na stronie). */
  wynikow: number;
  wszystkich: number;
  /** Nagłówek starego panelu: CAŁY zbiór — nie reaguje na filtry ani na stronę. */
  podsumowanie: { liczbaKlientow: number; sumaPortfeliGrosze: number };
  slowniki: { role: RolaKonta[]; miasta: string[]; szkoly: string[] };
  pobrano: string;
}

export interface DoladowanieWynik {
  ok: true;
  uzytkownikId: string;
  operacjaId: string;
  kwotaGrosze: number;
  saldoPrzedGrosze: number;
  /** Odczyt systemu głównego PO operacji; `null` = konta nie ma już na liście. */
  saldoPoGrosze: number | null;
}

export interface UsuniecieKontaWynik {
  ok: true;
  uzytkownikId: string;
  /** `true` = konta nie ma na liście systemu głównego po operacji (odczyt, nie założenie). */
  usuniete: boolean;
}

export interface AudytAdministracjiWpis {
  czas: string;
  aktor: { id: string | number | null; email: string | null; rola: string | null };
  operacja: 'DOLADOWANIE' | 'USUNIECIE_KONTA' | 'PRZYPISZ_SZKOLE' | 'ODEPNIJ_SZKOLE' | 'ZATWIERDZ_DANIE' | 'ODRZUC_DANIE' | string;
  encja: Record<string, unknown>;
  przed: unknown;
  po: unknown;
  wynik: string;
  korelacja: string | null;
}

export interface AudytAdministracjiOdpowiedz {
  wpisy: AudytAdministracjiWpis[];
}

// ── ADMINISTRACJA → SZKOŁY I RESTAURACJE ────────────────────────────────────
//
// Relacja restauracja ↔ szkoła z systemu głównego (`SchoolRestaurant`), czytana tym samym wywołaniem
// co Grafik. Dni i godzinę zamknięcia zmienia się w Grafiku — tu są informacyjnie.

export interface SzkolaPary {
  id: number;
  nazwa: string;
  /** Liczba zaznaczonych dni pon–pt w Grafiku. */
  dniDostaw: number;
  /** 'HH:MM' albo `null` = godziny nie znamy (nigdy „do północy"). */
  zamkniecieZamowien: string | null;
}

export interface SzkolaDoDodania {
  id: number;
  nazwa: string;
  adres: string | null;
}

export interface RestauracjaSzkol {
  id: number;
  nazwa: string;
  szkoly: SzkolaPary[];
  /** Szkoły z miasta restauracji: nieusunięte, potwierdzone, niezablokowane, jeszcze nieprzypisane. */
  doDodania: SzkolaDoDodania[];
}

export interface MiastoSzkol {
  /** `null` = restauracja bez miasta w systemie głównym (na końcu listy). */
  id: number | null;
  nazwa: string;
  restauracje: RestauracjaSzkol[];
}

export interface SzkolyIRestauracjeOdpowiedz {
  miasta: MiastoSzkol[];
  /** `false` = API systemu głównego nie jest podłączone; zapisy odmówią jawnie. */
  zapisPodlaczony: boolean;
  odczytano: string;
}

export interface PrzypisanieSzkolyWynik {
  ok: true;
  /** Para odczytana z systemu głównego PO zapisie. */
  para: GrafikPara | null;
  potwierdzono: boolean;
}

export interface OdpiecieSzkolyWynik {
  ok: true;
  odpieto: boolean;
}

// ── ADMINISTRACJA → MODERACJA MENU (stary panel admina „Menu") ──────────────

/**
 * Propozycja zmiany dania (`MealChangeRequest`) — pola widoków systemu głównego: oczekująca,
 * zatwierdzona przez AI (`approvedAt`) i rozstrzygnięta (`resolvedAt`, `resolution`, `status`).
 * Ceny w groszach. `aiRiskFlags` przychodzi jako tekst rozdzielany przecinkami.
 */
export interface PropozycjaZmianyDania {
  id: number;
  createdAt: string | null;
  waitingHours: number | null;
  kind: string | null;
  mealId: number | null;
  mealName: string | null;
  restaurantId: number | null;
  restaurantName: string | null;
  aiDecision: string | null;
  aiConfidence: number | null;
  aiRiskFlags: string | string[] | null;
  aiReason: string | null;
  aiModel: string | null;
  imageAssessment: string | null;
  imageAssessmentLabel: string | null;
  imageReason: string | null;
  oldName: string | null;
  newName: string | null;
  oldDescription: string | null;
  newDescription: string | null;
  oldIngredients: string | null;
  newIngredients: string | null;
  oldPrice: number | null;
  newPrice: number | null;
  oldImageUrl: string | null;
  imageUrl: string | null;
  notificationDelivered: boolean | null;
  hasNewerPending: boolean | null;
  mealDeleted: boolean;
  mealConfirmed: boolean | null;
  approvedAt: string | null;
  resolvedAt: string | null;
  resolvedBy: string | null;
  status: string | null;
  resolution: string | null;
  operatorId: string | null;
}

export interface StronaPropozycji {
  pozycje: PropozycjaZmianyDania[];
  wszystkich: number;
  strona: number;
  rozmiar: number;
  /** Tylko kolejka: ile pozycji bez doręczonego powiadomienia operatora. */
  bezPowiadomienia: number | null;
  /** Tylko kolejka: ile pozycji dotyczy usuniętego dania. */
  zUsunietymDaniem: number | null;
  filtr: string | null;
}

export type FiltrHistoriiModeracji = 'all' | 'auto' | 'operator' | 'approved' | 'rejected' | 'superseded';

/** Danie z zakładki „Do potwierdzenia" (legacy: dania nieopublikowane). */
export interface DanieDoPotwierdzenia {
  id: number;
  nazwa: string | null;
  opis: string | null;
  skladniki: string | null;
  tagi: string[];
  zdjecie: string | null;
  cenaGrosze: number | null;
  liczbaPosilkow: number | null;
  restauracja: { id: number | null; nazwa: string | null };
}

export interface LicznikiModeracji {
  doPotwierdzenia: number;
  kolejka: number;
  /** Znacznik „!" starego panelu: nowe dania (Creation) w kolejce ręcznej. */
  noweDaniaWKolejce: number;
}

export type RodzajDecyzjiModeracji = 'propozycja' | 'danie';

export interface DecyzjaModeracjiWynik {
  ok: true;
  rodzaj: RodzajDecyzjiModeracji;
  id: number;
  /** Propozycja: rozstrzygnięcie systemu głównego (Applied / AlreadyResolved); danie legacy: ZAPISANO. */
  wynik: string;
}

// ── OPINIE ───────────────────────────────────────────────────────────────────
//
// Siedem kolumn, wszystkie filtrowalne, ŻADNA sortowalna — zmierzone ze starego panelu.
// Podsumowanie liczy się po WIERSZACH PO FILTRACH, nie po całym zbiorze: procent ma
// odpowiadać tabeli, która jest pod nim.

export interface Opinia {
  data: string | null;
  szkola: string | null;
  danie: string | null;
  uczen: string | null;
  restauracja: string | null;
  rodzaj: 'Pozytywna' | 'Negatywna';
  komentarz: string | null;
}

export interface OpiniePodsumowanie {
  pozytywne: number;
  negatywne: number;
  /** Jedno miejsce po przecinku — jak `toFixed(1)` w starym panelu. */
  procent_negatywnych: number;
  wierszy: number;
}

export interface OpinieOdpowiedz {
  opinie: Opinia[];
  podsumowanie: OpiniePodsumowanie;
  /** `false` = replika opinii jeszcze nie istnieje (zależność od strumienia R5-04). */
  zrodlo_podlaczone: boolean;
  zaleznosc: { strumien: string; czego_brak: string; skutek: string } | null;
}

export interface OpinieZapytanie {
  date_from?: string;
  date_to?: string;
  szkola?: string;
  danie?: string;
  uczen?: string;
  restauracja?: string;
  rodzaj?: string;
  komentarz?: string;
}

// ── PANEL RESTAURACJI ────────────────────────────────────────────────────────
//
// „Dziś" liczone jest po DNIU DOSTAWY, nie po dniu złożenia zamówienia — restauracja pyta,
// co ma ugotować. Pozycje bez daty dostawy nie wchodzą do dzisiejszego dnia i są raportowane
// osobno, żeby brak nie zniknął po cichu.

export interface DzisSzkola {
  szkola_id: number | null;
  szkola: string;
  zamowien: number;
  posilkow: number;
}

export interface DzisDanie {
  danie: string;
  sztuk: number;
}

export interface DzisRozbicie {
  szkola_id: number | null;
  szkola: string;
  razem: number;
  dania: { danie: string; sztuk: number }[];
}

/** Jeden kurs kierowcy — albo jawny stan „nie wiemy, którym kursem to jedzie". */
export interface DzisWyjazd {
  podsumowanie: { zamowien: number; posilkow: number; szkol: number; pozycji: number };
  wg_szkol: DzisSzkola[];
  wg_dan: DzisDanie[];
}

/**
 * Podział dnia na dwa kursy według godziny zamknięcia zamówień SZKOŁY.
 *
 * `bez_cutoffu` nie jest resztą ani błędem — to odpowiedź „grafik nie mówi, o której ta szkoła
 * zamyka zamówienia". Dopisanie takich pozycji do pierwszego kursu byłoby zgadywaniem, którego
 * kuchnia nie miałaby jak zauważyć, więc ekran pokazuje ten stan osobno i pod nazwą.
 */
export interface DzisWyjazdy {
  granica: string;
  regula: string;
  zrodlo_cutoffu: string;
  pierwszy: DzisWyjazd;
  drugi: DzisWyjazd;
  bez_cutoffu: DzisWyjazd;
}

export interface DzisiejszeZamowienia {
  dzien: string;
  restauracja_id: number;
  /** Nazwa restauracji z repliki CRM (nagłówek wydruku); starszy serwer jej nie podaje. */
  restauracja_nazwa?: string | null;
  podsumowanie: { zamowien: number; posilkow: number; szkol: number; pozycji: number };
  wg_szkol: DzisSzkola[];
  wg_dan: DzisDanie[];
  rozbicie_szkola_danie: DzisRozbicie[];
  /** Pozycje, których dnia dostawy nie znamy. NIE są wliczone do „dziś". */
  pozycji_bez_daty_dostawy: number;
  /** Dwa kursy kierowcy. Pole może NIE PRZYJŚĆ ze starszego serwera — ekran to znosi. */
  wyjazdy?: DzisWyjazdy;
  podstawa: { dzien_liczony_wg: string; filtr: string };
}

export interface SzkolaRestauracji {
  szkola_id: number;
  szkola: string;
  dni_aktywne: number[];
  godzina_jednolita: boolean;
  godzina: string | null;
}

export interface SzkolyRestauracji {
  restauracja_id: number;
  szkoly: SzkolaRestauracji[];
  /** Zawsze `true` — przypisania zmienia się w Administracja → Szkoły i restauracje, dni w Administracja → Grafik. */
  tylko_odczyt: boolean;
  gdzie_sie_zmienia: string;
}

// ── ZDOLNOŚCI ŚRODOWISKA ─────────────────────────────────────────────────────
//
// Co NAPRAWDĘ działa w tym wdrożeniu. Front nie zgaduje i nie próbuje „na sucho": przycisk,
// który wygląda na czynny i kończy się błędem, jest gorszy niż wyłączony z podanym powodem.

export interface Zdolnosci {
  tryb: string | null;
  zdolnosci: {
    transakcje_kwota_pobrana: boolean;
    opinie: boolean;
    menu_moderacja: boolean;
    dania_restauracji: boolean;
    historia_cen: boolean;
    moderacja_zatwierdz_odrzuc: boolean;
    transakcje_zwrot_usun: boolean;
    /** Administracja → Szkoły i restauracje: dodanie i odpięcie szkoły restauracji. */
    szkoly_restauracji_zapis: boolean;
    /** Administracja → Użytkownicy: doładowanie portfela i usunięcie konta klienta. */
    konta_zapis: boolean;
    /** Operacje, których API systemu głównego nie ma — zawsze `false`, nie do włączenia. */
    organizacja_przypisz: boolean;
    dania_zapis: boolean;
    menu_zapis: boolean;
  };
  brakujace: {
    tabele: { nazwa: string; po_co: string }[];
    kolumny: { nazwa: string; po_co: string }[];
    api_systemu_glownego: string[];
    operacje_api: { nazwa: string; strumien: string }[];
  };
}


// ── MENU NA KONKRETNY DZIEŃ (parytet ze starym panelem: dbo.DailyMenus) ──────
export interface PozycjaMenuDnia {
  meal_id: number;
  nazwa: string | null;
  opis: string | null;
  skladniki: string | null;
  cena_grosze: number | null;
  sztuk: number | null;
  danie_dnia: boolean;
  nowe: boolean;
}

/** Cztery stany źródła — front ma je NAZYWAĆ, nie pokazywać gołe zero. */
export type StanZrodlaMenuDnia = 'OK' | 'SOURCE_UNAVAILABLE' | 'ACCESS_DENIED' | 'SYNC_STALE';

export interface MenuDnia {
  data: string;
  data_numeryczna: number;
  restauracja_id?: number;
  pozycje: PozycjaMenuDnia[];
  szkoly: { szkola_id: number; nazwa: string | null }[];
  pusty_biznesowo?: boolean;
  zrodlo: { stan: StanZrodlaMenuDnia; swiezosc: string | null; sla_s: number };
  zaleznosc?: { strumien: string; czego_brak: string } | null;
}

// ── DANIA RESTAURACJI ────────────────────────────────────────────────────────

/** Trzy stany, nie dwa. `IsConfirmed` w źródle jest `bool?` i NULL znaczy „oczekuje". */
export type StanDania = 'OCZEKUJE' | 'ZATWIERDZONE' | 'ODRZUCONE';

export interface DanieRestauracji {
  danie_id: number;
  nazwa: string | null;
  opis: string | null;
  skladniki: string | null;
  cena_grosze: number | null;
  ilosc: number | null;
  zdjecie_url: string | null;
  /** Trzy stany ze źródła — nigdy `true/false/null`, żeby nie dało się ich pomylić. */
  stan: StanDania;
  zmieniono: string | null;
}

export interface DaniaRestauracji {
  restauracja_id?: number;
  dania: DanieRestauracji[];
  zrodlo_podlaczone: boolean;
  zapis_podlaczony: boolean;
  zaleznosc?: { strumien: string; czego_brak: string; skutek: string } | null;
}

/*
 * ── RAPORTY DOSTAW RESTAURACJI ────────────────────────────────────────────────
 *
 * Dane pochodzą z systemu kierowców i są liczone JEGO modułami. Control Center dokłada
 * wyłącznie zawężenie do jednego najemcy — po stronie serwera, w klauzuli zapytania.
 *
 * `punktualnosc_pct` i `trafnosc_zgloszen_pct` to DWIE RÓŻNE MIARY:
 *   punktualność — czy kierowca dowozi na czas,
 *   trafność     — czy prawidłowo uprzedza, że się spóźni.
 * Ekran nigdy nie łączy ich w jedną „ocenę", bo odpowiadają na inne pytania.
 */
export interface ZapytanieRaportuDostaw {
  restauracja?: number;
  od?: string;
  do?: string;
  kierowca?: number;
  szkola?: number;
  status?: string;
  punktualnosc?: 'spoznione' | 'na_czas';
  /** Czy przy dostawie było zgłoszenie spóźnienia. Filtr ZBIORU, egzekwowany na serwerze. */
  zgloszenie?: 'tak' | 'nie';
  /**
   * Trafność zgłoszenia — TRZY stany, nie dwa.
   * `nierozstrzygalne` to zgłoszenie o poziomie, którego model nie zna; wrzucenie go do
   * „nietrafnych" obciążałoby kierowcę za lukę w danych.
   */
  trafnosc?: 'trafne' | 'nietrafne' | 'nierozstrzygalne';
  strona?: number;
  rozmiar?: number;
}

function paramsRaportu(z: ZapytanieRaportuDostaw): Record<string, string | number> {
  const p: Record<string, string | number> = {};
  if (z.restauracja) p.restauracja = z.restauracja;
  if (z.od) p.od = z.od;
  if (z.do) p.do = z.do;
  if (z.kierowca) p.kierowca = z.kierowca;
  if (z.szkola) p.szkola = z.szkola;
  if (z.status) p.status = z.status;
  if (z.punktualnosc) p.punktualnosc = z.punktualnosc;
  if (z.zgloszenie) p.zgloszenie = z.zgloszenie;
  if (z.trafnosc) p.trafnosc = z.trafnosc;
  if (z.strona && z.strona > 1) p.strona = z.strona;
  if (z.rozmiar) p.rozmiar = z.rozmiar;
  return p;
}

/** Zakres dat, o który serwer FAKTYCZNIE zapytał. Ekran pokazuje tę wartość, nie swoją. */
export interface ZakresRaportu {
  od: string;
  do: string;
}

/**
 * Wspólna część każdej odpowiedzi raportów.
 *
 * `zrodlo_podlaczone: false` NIE oznacza „zero dostaw". Oznacza, że nie było czego zmierzyć —
 * i ekran ma to powiedzieć innymi słowami niż pusty okres.
 */
interface OdpowiedzRaportu {
  restauracja_id: number;
  zrodlo_podlaczone: boolean;
  powod_braku?: string;
  wyjasnienie?: string;
  zakres: ZakresRaportu;
}

export interface KpiDostaw {
  dowozow: number;
  na_czas: number;
  spoznionych: number;
  spoznionych_5: number;
  spoznionych_15: number;
  spoznionych_powyzej_15: number;
  porownywalnych: number;
  punktualnosc_pct: number | null;
  srednie_spoznienie_min: number | null;
  najwieksze_spoznienie_min: number | null;
  przed_czasem: number;
  reczne_przed_przerwa: number;
  nieklasyfikowalne: number;
  anomalie_czasu: number;
  zgloszen: number;
  zgloszen_dopasowanych: number;
  zgloszen_trafnych: number;
  zgloszen_nietrafnych: number;
  trafnosc_zgloszen_pct: number | null;
  srednie_odchylenie_min_wszystkie: number | null;
}

/**
 * ZASIĘG KPI — deklarowany przez serwer, nie zgadywany przez ekran.
 *
 * KPI liczą się dla całego okresu i zakresu najemcy; filtry tabeli ich NIE zawężają.
 * Tak robi to system kierowców i tak ma zostać — ale ekran MUSI to napisać. Tabela zawężona
 * i KPI z innego zbioru, bez słowa wyjaśnienia, to dwie liczby poprawne osobno i sprzeczne
 * razem; operator nie ma szans tego rozpoznać.
 */
export interface ZasiegKpi {
  zakres: string;
  filtry_tabeli_stosowane: boolean;
  opis: string;
}

export interface PodsumowanieDostaw extends OdpowiedzRaportu {
  restauracja?: string;
  kpi?: KpiDostaw;
  zasieg_kpi?: ZasiegKpi;
  zastrzezenia?: readonly string[];
}

export interface KierowcaDostaw {
  kierowca_id: number | null;
  kierowca: string;
  dowozow: number;
  na_czas: number;
  spoznionych: number;
  porownywalnych: number;
  punktualnosc_pct: number | null;
  srednie_spoznienie_min: number | null;
  najwieksze_spoznienie_min: number | null;
  zgloszen: number;
  zgloszen_dopasowanych: number;
  zgloszen_trafnych: number;
  zgloszen_nietrafnych: number;
  trafnosc_zgloszen_pct: number | null;
}

export interface KierowcyDostaw extends OdpowiedzRaportu {
  kierowcy?: KierowcaDostaw[];
  objasnienie?: { punktualnosc: string; trafnosc: string };
}

export interface SzkolaDostaw {
  szkola_id: number | null;
  szkola: string;
  dowozow: number;
  na_czas: number;
  spoznionych: number;
  punktualnosc_pct: number | null;
  najwieksze_spoznienie_min: number | null;
}

export interface SzkolyDostaw extends OdpowiedzRaportu {
  szkoly?: SzkolaDostaw[];
}

export interface DzienDostaw {
  dzien: string;
  dowozow: number;
  na_czas: number;
  spoznionych: number;
  punktualnosc_pct: number | null;
  najwieksze_spoznienie_min: number | null;
}

export interface DniDostaw extends OdpowiedzRaportu {
  dni?: DzienDostaw[];
}

export interface WierszDostawy {
  id: number;
  dzien: string;
  planowana: string | null;
  dostarczono: string | null;
  status: string;
  odchylenie_min: number | null;
  zrodlo_wpisu: string | null;
  w_oknie: boolean | null;
  kierowca_id: number | null;
  kierowca: string;
  szkola_id: number | null;
  szkola: string;
  zgloszenie: {
    zgloszono: string | null;
    zgloszony_poziom: string;
    /** `null` = nie da się rozstrzygnąć. To NIE jest to samo, co „nietrafne". */
    trafne: boolean | null;
  } | null;
}

export interface ListaDostaw extends OdpowiedzRaportu {
  dostawy?: WierszDostawy[];
  strona?: number;
  rozmiar_strony?: number;
  /** Liczba wierszy PO FILTRACH, przed stronicowaniem — czyli ta, którą wolno zacytować. */
  wierszy_ogolem?: number;
  /**
   * Echo filtrów, które serwer FAKTYCZNIE zastosował. Filtr odrzucony po cichu (bo wartość nie
   * przeszła walidacji) dałby tabelę szerszą, niż operator zamawiał — i nic by mu tego nie
   * powiedziało. Tutaj widać różnicę.
   */
  filtry_zastosowane?: {
    kierowca: number | null;
    szkola: number | null;
    status: string | null;
    punktualnosc: string | null;
    zgloszenie: string | null;
    trafnosc: string | null;
  };
}

export interface FiltryDostaw extends OdpowiedzRaportu {
  kierowcy?: { id: number; nazwa: string }[];
  szkoly?: { id: number; nazwa: string }[];
  statusy?: string[];
  zgloszenia?: string[];
  trafnosci?: string[];
}

/*
 * ── HISTORIA TRANSAKCJI RESTAURACJI ───────────────────────────────────────────
 *
 * Ziarno to POZYCJA ZAMÓWIENIA, nie zamówienie: restauracja rozlicza się z ugotowanych porcji.
 * Danych osobowych kupującego tu NIE MA i nie jest to przeoczenie — administrator widzi nazwę
 * klienta, bo odpowiada na reklamacje; restauracja rozlicza się z posiłków, a nie z osób.
 *
 * `suma_grosze` to suma cen katalogowych pozycji, a NIE kwota pobrana od klienta. Serwer
 * dosyła `podstawa` z tym zdaniem i ekran ma je pokazać — „kwota" bez podstawy bywa czytana
 * jako przychód i wtedy rozjeżdża się z księgowością.
 */
export interface ZapytanieTransakcjiRestauracji {
  restauracja?: number;
  od?: string;
  do?: string;
  /** Kod statusu ze słownika źródła. Wartość spoza słownika serwer IGNORUJE, nie zawęża do pustki. */
  status?: string;
  skasowane?: 'tylko' | 'bez';
  strona?: number;
}

export interface TransakcjaRestauracji {
  pozycja_id: number;
  data: string | null;
  data_dostawy: string | null;
  szkola: string | null;
  danie: string | null;
  ilosc: number | null;
  cena_grosze: number | null;
  /** Kod statusu ze źródła (`dbo.[Transaction].Status`). Do filtrowania i uzgodnień. */
  status_kod: string | null;
  /**
   * Etykieta gotowa do pokazania. Uwzględnia skasowanie POZYCJI: transakcja mogła być
   * „Zrealizowano", a ta pozycja wycofana — i wtedy status transakcji kłamałby o wydaniu dania.
   */
  status: string | null;
  skasowana: boolean;
}

export interface TransakcjeRestauracji {
  restauracja_id: number;
  wiersze: TransakcjaRestauracji[];
  strona: number;
  rozmiar_strony: number;
  wierszy_ogolem: number;
  /** Słownik statusów ze źródła: kod → etykieta. Front nie zgaduje możliwych wartości. */
  statusy?: Record<string, string>;
  podsumowanie: {
    sztuk: number;
    suma_grosze: number | null;
    /** Ile wierszy weszło do sumy. Reszta jest w tabeli, ale poza sumą. */
    wierszy_zrealizowanych?: number;
    /** Zdanie wprost o tym, CZYM jest suma i CO do niej weszło. Pokazywane dosłownie. */
    podstawa: string;
  };
  correlation_id?: string;
}

/**
 * Odpowiedź wejścia właściciela w panel restauracji.
 *
 * `tylko_odczyt` przychodzi z serwera i jest tam EGZEKWOWANE — nie jest podpowiedzią dla
 * interfejsu. Powłoka pokazuje je operatorowi, żeby wiedział, w jakim trybie patrzy.
 */
export interface LogowanieMistrza {
  tryb: 'OWNER_RESTAURANT_MASTER_SESSION';
  tylko_odczyt: boolean;
  restauracja: {
    restauracja_id: number;
    nazwa: string;
    login: string;
    miasto: string | null;
  };
  wygasa_za_sek: number;
}

// ── PODGLĄD RESTAURACJI PRZEZ WŁAŚCICIELA ────────────────────────────────────
//
// To NIE jest logowanie jako restauracja. Właściciel potwierdza WŁASNE hasło, a serwer wydaje
// krótką sesję ograniczoną do jednej restauracji. Hasła restauracji nie znamy i nie potrzebujemy.

export interface ZnalezionaRestauracja {
  restauracja_id: number;
  nazwa: string;
  login: string;
  miasto: string | null;
}

export interface WynikSzukania {
  restauracje: ZnalezionaRestauracja[];
  wiecej_niz_pokazano: boolean;
}

export interface StanPodgladu {
  aktywny: boolean;
  restauracja_id: number | null;
  nazwa: string | null;
}

export interface WejscieWPodglad {
  podglad: {
    restauracja_id: number;
    nazwa: string;
    login: string;
    wygasa_za_sek: number;
  };
}

export interface ParytetSlowniki {
  statusy: string[];
  typy: string[];
  typ_domyslny: string;
  sortowalne: string[];
  filtrowalne_tekstowo: string[];
  rozmiar_strony: number;
}

// ── Katalog dań ──────────────────────────────────────────────────────────────

export interface CatalogDish {
  dish_id: number;
  restaurant_id: number | null;
  restaurant_name: string | null;
  name: string | null;
  description: string | null;
  ingredients: string | null;
  list_price_grosze: number | null;
  is_active: boolean;
  first_seen_at: string | null;
  last_seen_at: string | null;
  source: string | null;
  order_item_rows: number;
  /** Danie bez sprzedaży ZOSTAJE w katalogu i jest tak oznaczone. */
  has_sales: boolean;
}

/**
 * Proweniencja ODCZYTU, z którego powstał katalog — nie zapisu.
 * Bez niej ekran umie pokazać wynik (13 dań) i nie ma czym wyjaśnić, że stoi za nim 364 wiersze
 * menu dziennego. „Liczba dań" mylona z „liczbą wierszy karty" to najkosztowniejsze
 * nieporozumienie w tej domenie: karta jest KOPIOWANA na kolejne dni.
 */
export interface CatalogSourceMeta {
  zrodlo: string;
  baza: string;
  /** Data karty w kodowaniu źródła: rok*100000 + miesiąc*100 + dzień. */
  data_karty: number[];
  szkol_w_zrodle: number;
  szkol_z_menu: number;
  szkol_bez_menu: number;
  szkol_nieodczytanych: number;
  surowe_wystapienia_menu: number;
  unikalnych_dan: number;
  restauracji: number;
  regula_deduplikacji: string;
  /** Zakres, jaki to źródło NAPRAWDĘ pokrywa — osobno dla każdego słownika. */
  zakres_szkol?: ZakresZrodla;
  zakres_restauracji_kod?: ZakresZrodla;
  zakres_dan?: ZakresZrodla;
  /** Opis słowny ograniczenia zakresu restauracji (zostaje dla czytelności dowodu). */
  zakres_restauracji?: string;
  /** Szkoły, których NIE DAŁO SIĘ odczytać — to co innego niż „nie mają dziś karty". */
  szkoly_nieodczytane_lista?: { id: number; name: string | null; powod: string }[];
}

/**
 * ZAKRES ŹRÓDŁA — pole, którego brak pozwalał czytać częściowy słownik jak pełne dane wzorcowe.
 *
 * Publiczne API Main TEST ma dokładnie dwa wejścia: `/cities` i `/schools/{id}/meals`. Listy
 * restauracji NIE MA. Dlatego słownik szkół jest kompletny w rozumieniu katalogu publicznego,
 * a słownik restauracji zawiera WYŁĄCZNIE te, które wystąpiły w karcie z dnia odczytu — i nie
 * wolno go nazywać rejestrem restauracji TuSzamy.
 */
export type ZakresZrodla =
  | 'WSZYSTKIE_SZKOLY_KATALOGU_PUBLICZNEGO'
  | 'RESTAURACJE_Z_BIEZACEJ_KARTY'
  | 'BIEZACY_KATALOG_DAN'
  | 'NIEUSTALONY';

export interface ReferenceSchool {
  school_id: number | null;
  name: string | null;
  city: string | null;
  address: string | null;
  break_time: string | null;
  is_blocked: boolean;
  synced_at: string | null;
}

export interface ReferenceRestaurant {
  restaurant_id: number | null;
  name: string | null;
  city: string | null;
  phone: string | null;
  is_blocked: boolean;
  synced_at: string | null;
}

export interface DictionarySync {
  synced_at: string;
  upserted: number;
  source_meta: CatalogSourceMeta | null;
}

export interface SchoolsDictionaryResponse {
  count: number;
  blocked_count: number;
  source: string;
  dictionary_sync?: DictionarySync | null;
  schools: ReferenceSchool[];
}

export interface RestaurantsDictionaryResponse {
  count: number;
  blocked_count: number;
  source: string;
  dictionary_sync?: DictionarySync | null;
  restaurants: ReferenceRestaurant[];
}

export interface DishCatalogResponse {
  /**
   * Czy tabela katalogu w ogóle istnieje w tym środowisku. Na produkcji `crm_dishes` NIE
   * istnieje i nie ma decyzji, żeby ją zakładać — więc `false` jest tu stanem poprawnym,
   * nie awarią. Trzy różne zdania, których nie wolno mylić:
   *   catalog_available === false → katalogu nie ma gdzie czytać,
   *   count === 0                 → katalog jest, ale pusty,
   *   odrzucone żądanie           → odczyt się nie udał, warto spróbować ponownie.
   */
  catalog_available?: boolean;
  caveats?: string[];
  count: number;
  without_sales_count: number;
  /**
   * Liczba wierszy w CAŁYM rejestrze sprzedaży. Rozstrzyga, czy „danie bez sprzedaży" jest
   * pomiarem, czy tylko konsekwencją pustego źródła — te dwie sytuacje mają identyczną liczbę.
   */
  sales_source_rows?: number;
  sales_source_empty?: boolean;
  catalog_sync?: { synced_at: string; upserted: number; source_meta: CatalogSourceMeta | null } | null;
  source: string;
  dishes: CatalogDish[];
}

// ── RESTAURACJE → GRAFIK (kanoniczny, system główny) ─────────────────────────

export interface GrafikMiasto {
  id: number;
  nazwa: string;
}

export interface GrafikSzkolaKanon {
  id: number;
  nazwa: string;
  /** `false` = system główny nie podał nazwy szkoły (ekran pokazuje „?”, wiersza nie ukrywa). */
  nazwa_znana?: boolean;
  /** Miasto szkoły wg systemu głównego. `null` = system nie podał miasta (sekcja „Bez miasta”). */
  miasto_id: number | null;
}

export interface GrafikRestauracjaKanon {
  id: number;
  nazwa: string;
}

/** Para szkoła × restauracja: 7 dni (0 = pon … 6 = nd), `true` = zaznaczone w systemie głównym. */
export interface GrafikPara {
  szkola_id: number;
  restauracja_id: number;
  dni: boolean[];
  /** Godzina zamknięcia zamówień „HH:mm” (Europe/Warsaw) — tylko do odczytu. */
  zamkniecie_zamowien: string | null;
}

export interface GrafikKanonicznyOdpowiedz {
  miasta: GrafikMiasto[];
  szkoly: GrafikSzkolaKanon[];
  restauracje: GrafikRestauracjaKanon[];
  pary: GrafikPara[];
  odczytano: string;
  zapis_podlaczony: boolean;
  zrodlo: { system: string; odczyt: string; zapis: string; regula: string; konwencja_dni: string };
}

export interface GrafikZmiana {
  szkola_id: number;
  restauracja_id: number;
  dzien: number;
  zaznacz: boolean;
  /** Stan, który operator WIDZIAŁ — serwer porównuje go ze świeżym odczytem (konflikt). */
  przed: boolean;
}

export type GrafikStanZapisu =
  | 'ZAPISANO'
  | 'BEZ_ZMIAN'
  | 'KONFLIKT'
  | 'BRAK_PARY'
  | 'BRAK_GODZINY'
  | 'ODMOWA_SYSTEMU_GLOWNEGO'
  | 'ROZJAZD_PO_ZAPISIE'
  | 'ZAPISANO_BEZ_WERYFIKACJI';

export interface GrafikWynikZapisu {
  restauracja_id: number;
  szkola_id: number;
  stan: GrafikStanZapisu;
  powod?: string;
  dni_w_konflikcie?: number[];
  aktualne?: { dni: boolean[] } | null;
}

export interface GrafikZmianyOdpowiedz {
  wyniki: GrafikWynikZapisu[];
  zapisano: number;
  wszystkie_zapisane: boolean;
}

export interface GrafikAudytWpis {
  czas: string;
  aktor: string | null;
  etap: string;
  restauracja_id: number;
  szkola_id: number;
  dni_zmienione: number[];
  przed: boolean[];
  po: boolean[];
  powod?: string;
}

export interface GrafikAudytOdpowiedz {
  wpisy: GrafikAudytWpis[];
  trwalosc: string;
}

// ── RESTAURACJE → ROZLICZENIA MIESIĘCZNE ─────────────────────────────────────────────────────

export interface TydzienMiesiaca {
  isoYear: number;
  isoWeek: number;
  monday: string;
  friday: string;
  /** Ile z 5 dni pon–pt tygodnia leży w tym miesiącu (3, 4 albo 5). */
  daysInMonth: number;
}

export interface RozliczenieMiesiaca {
  settlementGroupId: string;
  settlementGroupName: string;
  isGroup: boolean;
  cities: string[];
  weeksTotal: number;
  weeksWithTurnover: number;
  turnoverByWeekGrosze: number[];
  turnoverGrosze: number;
  /** Jedna stawka marży netto całego rozliczenia; `null` = różne albo nieznana. */
  marginNetRate: number | null;
  marginsNet: number[];
  /** Kwoty FAKTURY TuSzamy do grosza (bez pełnych złotych); `null` z powodem, gdy nie da się ich policzyć. */
  marginNetGrosze: number | null;
  vatGrosze: number | null;
  invoiceGrossGrosze: number | null;
  exactMarginNet: string | null;
  amountsUnavailableReason: PowodBrakuKwot | null;
  weeksWithoutAmounts: { isoYear: number; isoWeek: number; monday: string; friday: string; reason: PowodBrakuKwot }[];
  members: {
    restaurantId: number;
    name: string;
    city: string | null;
    marginNet: number | null;
    turnoverByWeekGrosze: number[];
    turnoverGrosze: number;
  }[];
}

export interface RozliczeniaMiesiacOdpowiedz {
  period: {
    year: number;
    month: number;
    monthName: string;
    isDefault: boolean;
    weekRule: 'WEEK_BELONGS_TO_MONTH_WITH_MORE_OF_ITS_MON_FRI_DAYS';
    weeks: TydzienMiesiaca[];
    monthClosed: boolean;
  };
  rules: {
    commissionVatRate: number;
    warsaw: { cityId: number; city: string; marginNet: number };
    otherCities: { marginNet: number };
    weekToMonthRule: string;
    turnoverRule: string;
    rounding: 'INVOICE_TO_GROSZ';
    invoiceRule: string;
  };
  source: RozliczeniaOdpowiedz['source'];
  freshness: SwiezoscRozliczen;
  settlements: RozliczenieMiesiaca[];
  totals: {
    turnoverGrosze: number;
    turnoverByWeekGrosze: number[];
    marginNetGrosze: number | null;
    vatGrosze: number | null;
    invoiceGrossGrosze: number | null;
    settlementsWithTurnover: number;
    settlementsWithoutAmounts: number;
  };
  correlation_id?: string;
}

// ── RESTAURACJE → ROZLICZENIA ROCZNE ─────────────────────────────────────────────────────────

/** Metryki arkusza „Rentowność” dla tygodnia, miesiąca i roku (te same pola na każdym poziomie). */
export interface MetrykiRoku {
  turnoverGrosze: number;
  turnoverWroclawGrosze: number;
  turnoverMojeGrosze: number;
  positions: number;
  deliveryDays: number;
  /** Kwoty faktur do grosza; `null`, gdy choć jedno rozliczenie z obrotem nie ma kwot. */
  marginNetGrosze: number | null;
  vatGrosze: number | null;
  invoiceGrossGrosze: number | null;
  shareMoje: number | null;
  avgMealPriceGrosze: number | null;
  turnoverPerDayGrosze: number | null;
  positionsPerDay: number | null;
  profitabilityPct: number | null;
}

export interface TydzienRoku extends MetrykiRoku {
  isoYear: number;
  isoWeek: number;
  monday: string;
  friday: string;
  year: number;
  month: number;
  daysInMonth: number;
  future: boolean;
}

export interface MiesiacRoku extends MetrykiRoku {
  year: number;
  month: number;
  monthName: string;
  weeks: number;
  weeksFuture: number;
  closed: boolean;
  settlementsWithTurnover: number;
  settlementsWithoutAmounts: number;
}

export interface RozliczenieRoku {
  settlementGroupId: string;
  settlementGroupName: string;
  isGroup: boolean;
  cities: string[];
  members: { restaurantId: number; name: string; city: string | null }[];
  /** 12 wartości w kolejności miesięcy roku (IX..VIII). */
  turnoverByMonthGrosze: number[];
  positionsByMonth: number[];
  turnoverGrosze: number;
  positions: number;
  marginNetGrosze: number | null;
  invoiceGrossGrosze: number | null;
  missingAmountMonths: { year: number; month: number; reason: PowodBrakuKwot | null }[];
}

export interface RozliczeniaRokOdpowiedz {
  period: {
    startYear: number;
    endYear: number;
    label: string;
    isDefault: boolean;
    yearRule: 'SETTLEMENT_YEAR_SEPTEMBER_TO_AUGUST';
    weekRule: 'WEEK_BELONGS_TO_MONTH_WITH_MORE_OF_ITS_MON_FRI_DAYS';
    weeksRead: number;
    firstMonday: string | null;
    lastFriday: string | null;
  };
  rules: RozliczeniaMiesiacOdpowiedz['rules'] & {
    yearRule: string;
    wroclaw: { city: string; rule: string };
    positionsRule: string;
    deliveryDaysRule: string;
    amountsRule: string;
  };
  source: RozliczeniaOdpowiedz['source'];
  freshness: SwiezoscRozliczen;
  months: MiesiacRoku[];
  weeks: TydzienRoku[];
  totals: MetrykiRoku & {
    weeks: number;
    weeksFuture: number;
    monthsClosed: number;
    settlementsWithTurnover: number;
    settlementsWithoutAmounts: number;
  };
  settlements: RozliczenieRoku[];
  correlation_id?: string;
}
